import React, { useEffect } from "react";
import CloudBackground from "../community/shared/components/CloudBackground";
import PButton from "../../ui/input/PButton";

type GiftCheckoutCancelProps = {
  onBackToGift?: () => void;
  onBackToHome?: () => void;
  giftUrl?: string;
  homeUrl?: string;
};

const GiftCheckoutCancel: React.FC<GiftCheckoutCancelProps> = ({
  onBackToGift,
  onBackToHome,
  giftUrl = "/gift",
  homeUrl = "/",
}) => {
  // Add custom fonts for seriously nostalgic
  useEffect(() => {
    const preloadLink = document.createElement("link");
    preloadLink.rel = "preload";
    preloadLink.href = "/assets/fonts/SeriouslyNostalgicFn-Regular.otf";
    preloadLink.as = "font";
    preloadLink.type = "font/otf";
    preloadLink.crossOrigin = "anonymous";
    document.head.appendChild(preloadLink);

    const style = document.createElement("style");
    style.textContent = `
      @font-face {
        font-family: 'Seriously Nostalgic';
        src: url('/assets/fonts/SeriouslyNostalgicFn-Regular.otf') format('opentype');
        font-display: swap;
        font-weight: normal;
        font-style: normal;
      }
      
      .font-seriously-nostalgic { 
        font-family: 'Seriously Nostalgic', 'Playfair Display', 'Times New Roman', serif !important;
        font-weight: normal;
        font-style: normal;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      if (document.head.contains(preloadLink)) {
        document.head.removeChild(preloadLink);
      }
    };
  }, []);

  useEffect(() => {
    // Clear gift personalization form history on checkout cancellation
    try {
      localStorage.removeItem("giftPersonalizationForm");
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  }, []);

  return (
    <CloudBackground>
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12">
        <div className="text-center max-w-2xl mx-auto">
          {/* Cancel Icon */}
          <div className="text-6xl mb-8">💔</div>

          {/* Main Heading */}
          <h1 className="font-seriously-nostalgic text-[44px] sm:text-[52px] md:text-[64px] leading-tight text-white mb-6">
            Gift purchase
            <br />
            was cancelled
          </h1>

          {/* Cancel Message */}
          <div className="rounded-3xl border border-white/30 bg-white/15 backdrop-blur-xl p-8 mb-8 shadow-2xl">
            <p className="text-white text-lg leading-relaxed font-light mb-4">
              No worries! Your payment was not processed.
            </p>
            <p className="text-white/80 leading-relaxed">
              You can always come back and complete your gift purchase whenever
              you're ready.
              <br />
              <br />
              That special someone is still waiting for their perfect match! 💕
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <PButton
              onClick={onBackToGift}
              href={giftUrl}
              kind="primary"
              className="!bg-black hover:!bg-[#111111] !text-white !border-black px-12 py-4 text-lg font-medium rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              Try Again
            </PButton>

            <PButton
              onClick={onBackToHome}
              href={homeUrl}
              kind="secondary"
              className="!bg-white/15 hover:!bg-white/20 !text-white !border-white/40 px-12 py-4 text-lg font-medium rounded-full shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              Back to Home
            </PButton>
          </div>
        </div>
      </div>
    </CloudBackground>
  );
};

GiftCheckoutCancel.defaultProps = {
  onBackToGift: undefined,
  onBackToHome: undefined,
  giftUrl: "/gift",
  homeUrl: "/",
};

export default GiftCheckoutCancel;
